import { Link } from "@tanstack/react-router";
import { buildMonthGrid } from "@/lib/calendar/info";
import { cn } from "@/lib/utils";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName } from "@/i18n/holidays";
import { useToday } from "@/lib/calendar/today";
import { toISO } from "@/lib/calendar/dates";

interface Props {
  year: number;
  month: number; // 1-12
  /** Compact mode for year-grid (smaller cells, no extras) */
  compact?: boolean;
  /** Show pasaran + hijri sublabels */
  showSubLabels?: boolean;
  /** "fixed" pads to 6 weeks; "auto" trims trailing empty week rows. */
  align?: "auto" | "fixed";
}

export function MonthGrid({ year, month, compact, showSubLabels = true, align = "fixed" }: Props) {
  const cells = buildMonthGrid(year, month, align);
  const { t, locale, href } = useLocale();
  const monthName = t.common.months[month - 1];
  const daysShort = t.common.daysShort;
  const todayISO = useToday();

  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-card overflow-hidden",
        compact && "text-xs",
      )}
    >
      {!compact && (
        <div className="border-b border-border bg-card px-3 py-2 text-sm font-semibold">
          {monthName} {year}
        </div>
      )}
      <div className="grid grid-cols-7 bg-muted/40">
        {daysShort.map((d, i) => (
          <div
            key={d}
            className={cn(
              "px-1 py-1.5 text-center text-[10px] font-medium uppercase tracking-wide",
              i === 0 ? "text-red-date" : "text-muted-foreground",
            )}
          >
            {d}
          </div>
        ))}
      </div>
      <div className="grid auto-rows-fr grid-cols-7">
        {cells.map((cell, i) => {
          if (!cell.info) return <div key={i} className={cn(compact ? "h-8" : "min-h-20 sm:min-h-24", "border-t border-border")} />;
          const info = cell.info;
          const isRed = info.isTanggalMerah;
          const isCuti = info.isCutiBersama;
          const hasHariPenting = info.hariPenting.length > 0 && !info.isLibur;
          const isToday = todayISO === toISO(info.year, info.month, info.day);
          const fullName = info.holidays[0]?.name ?? info.hariPenting[0]?.name;
          const localizedFull = fullName ? translateHolidayName(fullName, locale) : undefined;
          // Show the FULL holiday name — no slicing/truncation. The cell
          // grows to fit; rows in the same week stay aligned because the
          // grid uses `auto-rows-fr`.
          const displayName = localizedFull;
          return (
            <Link
              key={i}
              to={href(info.pathDate) as never}
              title={localizedFull}
              className={cn(
                "group relative block border-t border-l border-border first:border-l-0 [&:nth-child(7n+1)]:border-l-0",
                "p-1.5 transition-colors hover:bg-accent/60",
                compact ? "h-8 text-center" : "min-h-20 sm:min-h-24",
                isRed && "bg-red-date-bg/40",
                isToday && "ring-2 ring-primary ring-inset z-10 bg-primary/5",
              )}
            >
              <div
                className={cn(
                  "flex items-baseline justify-between",
                  compact && "justify-center",
                )}
              >
                <span
                  className={cn(
                    "text-sm font-medium leading-none",
                    isRed && "text-red-date font-semibold",
                  )}
                >
                  {info.day}
                </span>
                {!compact && showSubLabels && (
                  <span className="text-[9px] text-muted-foreground">
                    {info.hijri.day}
                  </span>
                )}
              </div>
              {!compact && showSubLabels && locale === "id" && (
                <div className="mt-0.5 truncate text-[9px] text-muted-foreground">
                  {info.pasaran}
                </div>
              )}
              {!compact && displayName && (
                <div
                  className={cn(
                    "mt-0.5 break-words text-[10px] sm:text-[11px] font-medium leading-tight pr-3",
                    info.isLibur && "text-red-date",
                    isCuti && !info.isLibur && "text-cuti-bersama",
                    hasHariPenting && "text-muted-foreground",
                  )}
                >
                  {displayName}
                </div>
              )}
              {/* Bullet indicators */}
              <div className="absolute bottom-1 right-1 flex gap-0.5">
                {info.isLibur && <span className="h-1.5 w-1.5 rounded-full bg-red-date" />}
                {isCuti && <span className="h-1.5 w-1.5 rounded-full bg-cuti-bersama" />}
                {hasHariPenting && <span className="h-1.5 w-1.5 rounded-full bg-hari-penting" />}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function CalendarLegend() {
  const { t } = useLocale();
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full bg-red-date" /> {t.common.legendLibur}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full bg-cuti-bersama" /> {t.common.legendCuti}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full bg-hari-penting" /> {t.common.legendHari}
      </span>
    </div>
  );
}